// Lib
import { FC, useState, useEffect, useMemo } from "react";
// Api
import {
  useGetReportsQuery,
  useDeleteReportMutation,
  useLazyDownloadReportQuery,
} from "rtkQuery/query/reportsAPI";
// Hooks
import { useViewport, useNotification, useTable } from "hooks";
// Types
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";
import { TableAction } from "types/common";
import { ETable } from "types/tableFilters";
import { ReportItemDto } from "types/reports";
// Constants
import { rtkQueryParams } from "consts";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon, DownloadIcon, TrashIcon } from "icons";
import { Pagination, Table } from "components";
// Components
import { ConfirmDialog } from "components/Modals";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";

import { columns, filtersList } from "./config";

export const Reports: FC = () => {
  const { openNotification } = useNotification();

  const { isDesktop } = useViewport();

  const [isDeleteModal, setIsDeleteModal] = useState<ReportItemDto | false>(
    false,
  );

  const {
    sortingOrder,
    sortBy,
    page,
    limit,
    selectedFilters,
    debouncedFiltersQuery,
    setPage,
    setLimit,
    handleSort,
    handleSetTableFilterValues,
  } = useTable({ name: ETable.Reports });

  const {
    data: reportsData,
    isFetching: isReportsDataLoading,
    error: reportsError,
  } = useGetReportsQuery(
    {
      query: {
        page,
        limit,
        sortBy,
        sortingOrder,
        ...(debouncedFiltersQuery && debouncedFiltersQuery),
      },
    },
    rtkQueryParams,
  );

  useEffect(() => {
    if (reportsError) {
      errorHandler({ error: reportsError, openNotification });
    }
  }, [reportsError]);

  const [remove, { isLoading: isDeleteLoading }] = useDeleteReportMutation();

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sorter: any,
  ) => {
    handleSort(sorter?.field, sorter?.order);
  };

  const onConfirmDelete = async () => {
    if (!isDeleteModal) {
      return;
    }

    try {
      await remove({ id: isDeleteModal.id }).unwrap();

      openNotification({
        type: "success",
        message: "Report deleted successfully",
      });

      setIsDeleteModal(false);
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const actions: TableAction[] = useMemo(
    () => [
      {
        title: "",
        Icon: DownloadIcon,
        type: "Grey",
        onClick: (row: ReportItemDto) => {
          const link = document.createElement("a");
          link.href = row.downloadUrl;
          link.setAttribute("download", row.name);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
      },
      {
        title: "",
        Icon: TrashIcon,
        type: "Grey",
        onClick: (row: ReportItemDto) => setIsDeleteModal(row),
      },
    ],
    [],
  );

  return (
    <>
      <PageWrapper $fullwidth $column>
        <FlexContainer
          $fullwidth
          $column
          $grow={1}
          $padding={isDesktop && "0 0 32px"}
        >
          <FlexContainer
            $padding="0 0 24px"
            $align="center"
            $justify="space-between"
          >
            <Typography.H1>Reports</Typography.H1>
          </FlexContainer>

          <Table
            isLoading={isReportsDataLoading}
            withPagination={!isDesktop}
            dataSource={reportsData?.items || []}
            actions={actions}
            columns={columns}
            empty={{
              icon: DescriptionBlackIcon,
              title: "No reports to show.",
              description: "Try changing sections or change the filters",
            }}
            header={{
              totalCount: reportsData?.totalCount,
              filter: {
                filters: filtersList,
                selected: selectedFilters,
                setValue: handleSetTableFilterValues,
              },
            }}
            onChange={handleTableChange}
          />
        </FlexContainer>

        <Pagination
          onPage
          padding="12px 16px"
          limit={limit}
          page={page}
          setLimit={setLimit}
          setPage={setPage}
          showSizeChanger={!isDesktop}
          totalItems={reportsData?.totalCount}
        />
      </PageWrapper>

      <ConfirmDialog
        open={!!isDeleteModal}
        isLoading={isDeleteModal && isDeleteLoading}
        Icon={TrashIcon}
        message={`"${(isDeleteModal as ReportItemDto)?.name}" will be deleted`}
        description="Are you sure to continue this action?"
        onCancel={() => setIsDeleteModal(false)}
        firstCTAButton={{
          title: "Delete Report",
          status: "danger",
          loading: isDeleteLoading,
          onClick: onConfirmDelete,
        }}
      />
    </>
  );
};
